import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Badge } from 'components/ui/badge';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string | string[];
  onValueChange: (value: string | string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  emptyText = 'No options found.',
  multiple = false,
  disabled = false,
  className,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);

  const selectedValues = React.useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value : [];
    }
    return typeof value === 'string' ? [value] : [];
  }, [value, multiple]);

  const handleSelect = React.useCallback(
    (selectedValue: string) => {
      if (multiple) {
        const currentValues = Array.isArray(value) ? value : [];
        let newValues: string[];

        if (currentValues.includes(selectedValue)) {
          newValues = currentValues.filter((v) => v !== selectedValue);
        } else {
          newValues = [...currentValues, selectedValue];
        }

        onValueChange(newValues);
      } else {
        onValueChange(selectedValue === value ? '' : selectedValue);
        setOpen(false);
      }
    },
    [value, onValueChange, multiple]
  );

  const displayValue = React.useMemo(() => {
    if (selectedValues.length === 0) {
      return placeholder;
    }

    if (multiple) {
      if (selectedValues.length === 1) {
        const option = options.find((opt) => opt.value === selectedValues[0]);
        return option?.label || selectedValues[0];
      }
      return `${selectedValues.length} selected`;
    }

    const option = options.find((opt) => opt.value === selectedValues[0]);
    return option?.label || selectedValues[0];
  }, [selectedValues, options, placeholder, multiple]);

  const selectedLabels = React.useMemo(() => {
    return selectedValues
      .map((val) => {
        const option = options.find((opt) => opt.value === val);
        return option?.label || val;
      })
      .filter(Boolean);
  }, [selectedValues, options]);

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between min-h-10 h-auto py-2 text-base"
          >
            <span className="truncate text-left flex-1">{displayValue}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList
              onWheel={(e) => {
                const target = e.currentTarget;
                target.scrollTop += e.deltaY;
                e.preventDefault();
              }}
            >
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={handleSelect}
                    className="cursor-pointer text-base"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedValues.includes(option.value) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected items display for multiple selection */}
      {multiple && selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedLabels.map((label, index) => (
            <Badge
              key={`${selectedValues[index]}-${index}`}
              variant="secondary"
              className="text-sm cursor-pointer hover:bg-secondary/80"
              onClick={() => handleSelect(selectedValues[index])}
            >
              {label}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
